// TODO: this has a lot of sins - trying to be too many things at once
// we need t oeventually wrangle this logic in and break up as needed
import React, {
	useCallback,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react';
import clsx from 'clsx';
import type {AutocompleteProps, AutocompleteValue} from '@mui/material';
import {useWhenChangedEffect} from '@netx/core-components/hooks';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	SelectField,
	type SelectFieldProps,
} from '@netx/core-components/SelectField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {uniqueArray} from '@netx/core-next/utils/array';
import {addToArrayUnique} from '@netx/core-next/utils/array';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {
	AUTO_LIST_FIELD_CLASS_NAME,
	autoListFieldClasses,
	AutoListFieldClasses,
	getAutoListFieldUtilityClass,
} from './autoListFieldClasses';
import {AutoListFieldOption, AutoListFieldSelection} from './components';
import {IGNORE_INPUT_CHANGE_EVENTS} from './constants';
import {AutoListFieldLabel} from './locales';
import {isValidQueryInput, isValidUncontrolledInput} from './util';

export type AutoListFieldRootElement = HTMLDivElement;
export type AutoListFieldRef = React.ForwardedRef<AutoListFieldRootElement>;

type AutoListFieldOwnerState = WithClasses<
	AutoListFieldClasses,
	{
		labelHidden?: boolean;
	}
>;

const useUtilityClasses = (ownerState: AutoListFieldOwnerState) => {
	const {classes, labelHidden} = ownerState;
	const slots = {
		root: ['root', labelHidden && 'labelHidden'],
	};
	return composeClasses(slots, getAutoListFieldUtilityClass, classes);
};

// TODO: tests are choking here - TypeError: Cannot read properties of undefined (reading '__emotion_styles')
// @ts-ignore
const AutoListFieldRoot = styled(SelectField, {
	name: AUTO_LIST_FIELD_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	[`&.${autoListFieldClasses.labelHidden}`]: {
		'& .MuiInputBase-root.MuiFilledInput-root': {
			paddingTop: 0,
			height: 48,
		},
	},
})) as <
	T,
	Multiple extends boolean,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	props: SelectFieldProps<T, Multiple, DisableClearable, FreeSolo>,
) => JSX.Element;

const defaultIsInValue = <
	T,
	Multiple extends boolean = true,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	input: string | null,
	value: string | AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
	getValueLabel?: (value: any) => string,
) => {
	// Check that the normalizedValue is not already selected
	// values are not case-sensitive on the server and so not case-sensitive
	// in the values
	if (getValueLabel) input = getValueLabel(input);

	input = input == null ? '' : input.toLowerCase();
	// The selection is multiple and the option is already in the array
	if (Array.isArray(value))
		return value.some(
			(val) =>
				(getValueLabel ? getValueLabel(val) : val)?.toLowerCase() === input,
		);
	// Loose check for numbers/strings
	return (
		('' + (getValueLabel ? getValueLabel(value) : value))?.toLowerCase() ==
		input
	);
};

type IAutoListShared<
	T,
	Multiple extends boolean = true,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
> = {
	isValueTagRemovable?: (value: T) => boolean;
	getOptionLabel?: (option: T) => string;
	getOptionSecondaryLabel?: (value: T) => string;
	getOptionSelected?: (option: T, value?: T) => boolean;
	isInValue?: (
		input: string | T | null,
		value: string | AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
		getValueLabel?: (value: any) => string,
	) => boolean;
	testValueContainsInput?: (
		input: string | T | null,
		value?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
	) => boolean;
	testValueContainsOption?: (
		option: T,
		value?: string | AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
	) => boolean;
};

export interface AutoListCommonExtendProps<
	T,
	Multiple extends boolean = true,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>
	extends
		Omit<
			AutoListFieldProps<T, Multiple, DisableClearable, FreeSolo>,
			'filterOptions' | 'options'
		>,
		IAutoListShared<T, Multiple, DisableClearable, FreeSolo> {
	hideLabel?: boolean;
	name?: string;
	// will handle on their end before it ever gets here
	filterOptions?: (options: T[]) => T[];
}

export type AutolistLookupResults<T, Multiple, DisableClearable, FreeSolo> =
	Promise<AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>[]>;

export interface AutoListFieldProps<
	T,
	Multiple extends boolean = true,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>
	extends
		Omit<
			SelectFieldProps<T, Multiple, DisableClearable, FreeSolo>,
			'classes' | 'sx'
		>,
		WithClasses<AutoListFieldClasses>,
		IAutoListShared<T, Multiple, DisableClearable, FreeSolo> {
	clearOnClose?: boolean;
	changed?: boolean;
	/** User can input uncontrolled values */
	// TODO: just check for `freeSolo` prop?
	flexible?: boolean;
	hideLabel?: boolean;
	// value?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;

	placeholder?: string;
	getTagOptionLabel?: (option: T) => string;

	onSearch?: (value: string) => void;
}

const AutoListFieldInner = <
	T,
	Multiple extends boolean = true,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	{
		classes: _classes,
		className: className,
		changed,
		clearOnClose = true,
		disabled,
		flexible,
		hideLabel,
		label,
		loadingValue,
		// Multiple is opt out as single selection is used far less for lookups
		// at least for now anyways
		// And this is a pita to type out properly... still investigating what I might be missing
		multiple = true as Multiple,
		required,
		value,
		getOptionLabel,
		getOptionSecondaryLabel,
		isInValue = defaultIsInValue as any,
		filterOptions,
		onClose,
		onInputChange,
		onSearch,
		testValueContainsInput,
		testValueContainsOption,
		getTagOptionLabel,
		isValueTagRemovable,
		...props
	}: AutoListFieldProps<T, Multiple, DisableClearable, FreeSolo>,
	ref: AutoListFieldRef,
) => {
	let _value = !multiple
		? getOptionLabel
			? getOptionLabel(value as any)
			: value
		: '';
	if (_value == null) _value = '';

	// these are strings regardless of whatever value(s).
	// there is no mechanism, currently, for allowing the input of any
	// uncontrolled value that is more complex than a string
	const [uncontrolledValue, setUncontrolledValue] = useState('');
	const [uncontrolledValues, setUncontrolledValues] = useState<string[]>([]);

	type _AutoCompleteProps = Required<
		AutoListFieldProps<T, Multiple, DisableClearable, FreeSolo>
	>;

	const ownerState: AutoListFieldOwnerState = {
		classes: _classes,
		className,
		labelHidden: hideLabel,
	};

	const classes = useUtilityClasses(ownerState);

	// when our `changed` prop changes, reset the uncontrolled values
	// when the data is saved the `changed` prop is reset
	useEffect(() => {
		setUncontrolledValues([]);
	}, [changed]);

	// TODO: document/cleanup/remove what we can about these 3 methods
	// what problem are we solving other than uncontrolled (folsksonomy) values?
	// in any event, all the "uncontrolled value" stuff should be moved to a hook
	const testValueCb = useConstCallback(
		(
			input: string | T,
			value?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
		) => {
			if (testValueContainsInput) return testValueContainsInput(input, value);
			return Array.isArray(value) && isInValue(input, value, getOptionLabel);
		},
	);

	const testValueOptionCb = useConstCallback(
		(
			option: T,
			value?:
				| string
				| AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
		) => {
			return testValueContainsOption
				? testValueContainsOption(option, value)
				: typeof option === 'string' &&
						typeof value === 'string' &&
						isInValue(option, value as string);
		},
	);

	const testOptionsContainInputCb = useConstCallback(
		(inputValue: string, options: T[]) => {
			return options.some((option) =>
				testValueOptionCb(option, inputValue as any),
			);
		},
	);

	// When value changes check what of those values should be considered uncontrolled
	useWhenChangedEffect(value, (value) => {
		// when value changes, we want to wipe out the user input
		// not neccessary for single select as it will either have the value as the input
		// or the value will be unset
		if (multiple) onInputChange?.({}, '', 'reset');

		setUncontrolledValues((uncontrolledValues) => {
			// remove any uncontrolled values that no longer exist in or as `value`
			const newUncontrolledValues = uncontrolledValues.filter(
				(uncontrolledValue) => testValueCb(uncontrolledValue, value),
			);
			// check and see if our last uncontrolled value was responsible for `value` or any value within
			return isValidQueryInput(uncontrolledValue) &&
				testValueCb(uncontrolledValue, value)
				? addToArrayUnique(newUncontrolledValues, uncontrolledValue)
				: newUncontrolledValues;
		});
	});

	// Custom options filter
	// Note: `filterSelectedOptions` is separate from this and takes precedence
	const handleFilterOptions = useConstCallback<
		_AutoCompleteProps['filterOptions']
	>((options, state) => {
		if (props.loading) return options;

		const inputValue = state.inputValue.trim();

		if (filterOptions) {
			options = filterOptions(options, state);
		}

		if (
			flexible &&
			isValidUncontrolledInput(inputValue) &&
			// not already in options
			!testOptionsContainInputCb(inputValue, options) &&
			// not already in values
			!testValueCb(inputValue, value)
		) {
			// store our inputted value for tracking what selected options were
			// controlled and which were uncontrolled
			setUncontrolledValue(inputValue);
			// add to the top of the options list
			options.unshift(inputValue as any);
		} else {
			setUncontrolledValue('');
		}

		return options.filter(
			(option) => !multiple || !testValueCb(option as unknown as string, value),
		);
	});

	return (
		<AutoListFieldRoot<T, Multiple, DisableClearable, FreeSolo>
			data-testid="auto-list-field-autocomplete"
			ref={ref}
			// Even though we filter out already selected option
			// when running the query, we still need to filter out
			// selected options in between queries
			filterSelectedOptions={false}
			noOptionsText={
				props.inputValue == null ||
				props.inputValue === '' ||
				props.inputValue === '""' ||
				props.loadingOptions
					? props.loadingOptions
						? AutoListFieldLabel.LOADING
						: AutoListFieldLabel.NO_INPUT
					: AutoListFieldLabel.NO_MATCHES
			}
			renderOption={(props, option, state) => (
				<AutoListFieldOption
					secondary={getOptionSecondaryLabel?.(option)}
					value={getOptionLabel ? getOptionLabel(option) : (option as any)}
					uncontrolled={
						typeof option === 'string' && option === uncontrolledValue
					}
					{...props}
				/>
			)}
			renderTags={(value, getTagProps) => (
				<AutoListFieldSelection<T>
					disabled={disabled}
					isValueTagRemovable={isValueTagRemovable}
					getTagProps={getTagProps}
					getValueLabel={getTagOptionLabel ?? getOptionLabel}
					onSearch={onSearch}
					loading={loadingValue}
					values={value}
					uncontrolledValues={uncontrolledValues}
				/>
			)}
			{...props}
			filterOptions={handleFilterOptions}
			className={clsx(classes.root, className)}
			disabled={disabled}
			label={hideLabel ? undefined : label}
			loadingValue={loadingValue}
			multiple={multiple}
			required={required}
			value={value}
			getOptionLabel={getOptionLabel}
			onClose={splitEventHandler(onClose, (event) => {
				if (clearOnClose) {
					onInputChange?.(event, '', 'reset');
					return;
				}
				// put back current value
				if (!multiple)
					onInputChange?.(event, _value != null ? _value : '', 'reset');
			})}
			onInputChange={(event, str, reason) => {
				if (
					!event ||
					IGNORE_INPUT_CHANGE_EVENTS.includes(
						event.type as (typeof IGNORE_INPUT_CHANGE_EVENTS)[number],
					)
				)
					return;
				onInputChange?.(event, str, reason);
			}}
		/>
	);
};

export const AutoListFieldPrimitive = React.forwardRef(AutoListFieldInner) as <
	T,
	Multiple extends boolean,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	props: AutoListFieldProps<T, Multiple, DisableClearable, FreeSolo>,
) => JSX.Element;

export const AutoListField = AutoListFieldPrimitive;

export default AutoListField;
